import {getAxios} from "../axios";
import {addParam, errorCatch, getTokenHeaders} from "../helper";
import {
  ENDPOINT_DASHBOARD_REPORT,
  ENDPOINT_INFO_CARDS,
  ENDPOINT_ORGANIZATION_SALES_REPORT_PERIODS,
  ENDPOINT_ORGANIZATION_SALES_REPORT_ACTIVITY,
  ENDPOINT_ORGANIZATION_SALES_REPORT_PRODUCTS,
  ENDPOINT_ORGANIZATION_SALES_REPORT_SUMMARY,
  ENDPOINT_ORDER_REPORTS,
  ENDPOINT_PRODUCT_GROUPS,
  ENDPOINT_SALES_REPORT_ORGANIZATIONS,
  ENDPOINT_SALES_REPORT_PERIODS,
  ENDPOINT_SALES_REPORT_PRODUCTS,
  ENDPOINT_SALES_REPORT_SUMMARY
} from "../../config/endpoints";
import {Messages} from "../../config";
import qs from 'qs';


/**
 * List order reports
 * @param {{product_group_id: string, date_to: string, date_from: string}} payload
 * @param {string} payload.fromDate
 * @param {string} payload.toDate
 * @param {string} payload.productGroupId
 * @param {string} payload.aggBy
 * @returns {Promise<T>}
 */
export function getOrderReportsApi(payload) {
  let headersAndParams = getTokenHeaders(payload.pagination || null)
  return getAxios(false).get(
    addParam(
      ENDPOINT_ORDER_REPORTS,
      {
        '{order}': '',
      }),
    {
      headers: headersAndParams.headers,
      params: {
        ...headersAndParams.params,
        date_from: payload.fromDate,
        date_to: payload.toDate,
        product_group_id: payload.productGroupId,
        agg_by: payload.aggBy,
      }
    }
  )
    .then(response => {
      return response && response.data ? response.data : null
    })
    .catch(
      e => {
        errorCatch(e, {
          401: Messages.UNAUTHENTICATED_ERROR,
          403: Messages.UNAUTHORIZED_ERROR,
          404: Messages.NOT_FOUND_ERROR,
        });

        return e.response;
      }
    );
}

export function getProductGroups(payload) {
  if (payload && payload.disablePagination === true) {
    if (! payload.pagination) {
      payload.pagination = {};
    }

    payload.pagination.per_page = 100000;
  }

  return getAxios(false).get(
    addParam(
      ENDPOINT_PRODUCT_GROUPS,
      {
        '{productGroups}': '',
      }
    ),
    getTokenHeaders(payload?.pagination || null)
  )
    .then(response => {
      if (
        ! response ||
        ! response.data ||
        ! response.data.data ||
        response.status !== 200
      ) {
        return null;
      }

      // Reports intentionally include the complete commercial catalogue,
      // including archive products and product groups.
      return { data: response.data.data };
    })
    .catch(
      e => {
        errorCatch(e, {
          401: Messages.UNAUTHENTICATED_ERROR,
          403: Messages.UNAUTHORIZED_ERROR,
          404: Messages.NOT_FOUND_ERROR,
        });

        return e.response;
      }
    );
}

/**
 * Convert report UI state to the stable Admin sales-report request contract.
 * Include/exclude arrays stay as arrays and are encoded with bracket notation
 * by Axios. The UI-only `all` mode is represented by omitting that dimension.
 *
 * @param {object} filters
 * @returns {object}
 */
export function buildSalesReportParams(filters) {
  const params = {
    date_from: filters.dateFrom,
    date_to: filters.dateTo,
    timezone: filters.timezone,
    granularity: filters.granularity,
    compare: filters.compare
  };

  [
    ['organization_mode', 'organization_ids', filters.organizationMode, filters.organizationIds],
    ['product_mode', 'product_ids', filters.productMode, filters.productIds],
    ['product_group_mode', 'product_group_ids', filters.productGroupMode, filters.productGroupIds],
    ['order_type_mode', 'order_types', filters.orderTypeMode, filters.orderTypes]
  ].forEach(([modeKey, valuesKey, mode, values]) => {
    if (mode === 'include' || mode === 'exclude') {
      params[modeKey] = mode;
      params[valuesKey] = [...(values || [])];
    }
  });

  return params;
}

/**
 * Build the organization-scoped contract. The organization UUID is supplied
 * exclusively by the endpoint path; query-string organization overrides are
 * deliberately impossible even when stale or malicious UI state is passed.
 *
 * @param {object} filters
 * @returns {object}
 */
export function buildOrganizationSalesReportParams(filters) {
  const params = buildSalesReportParams(filters);

  delete params.organization_mode;
  delete params.organization_ids;

  return params;
}

/** Organization activity reporting is based on signature consumption, not
 * sales orders or catalogue rows. Keep its query allow-list independent from
 * sales reports so stale UI state cannot leak unsupported dimensions. */
export function buildActivityReportParams(filters) {
  return {
    date_from: filters.dateFrom,
    date_to: filters.dateTo,
    timezone: filters.timezone,
    granularity: filters.granularity
  };
}

function organizationSalesReportRequest(endpoint, organizationId, filters, extraParams, signal) {
  const headersAndParams = getTokenHeaders();

  return getAxios(false).get(addParam(endpoint, '{organization}', organizationId), {
    headers: headersAndParams.headers,
    params: {
      ...buildOrganizationSalesReportParams(filters),
      ...extraParams
    },
    paramsSerializer: params => qs.stringify(params, { arrayFormat: 'brackets' }),
    signal
  })
    .then(response => response && response.data ? response.data : null)
    .catch(e => {
      if (e && (e.code === 'ERR_CANCELED' || e.name === 'CanceledError')) {
        throw e;
      }

      errorCatch(e, {
        401: Messages.UNAUTHENTICATED_ERROR,
        403: Messages.UNAUTHORIZED_ERROR,
        404: Messages.NOT_FOUND_ERROR,
      });

      throw e;
    });
}

export function getOrganizationSalesSummary(organizationId, filters, signal) {
  return organizationSalesReportRequest(
    ENDPOINT_ORGANIZATION_SALES_REPORT_SUMMARY,
    organizationId,
    filters,
    {},
    signal
  );
}

export function getOrganizationSalesProducts(organizationId, filters, table, signal) {
  return organizationSalesReportRequest(
    ENDPOINT_ORGANIZATION_SALES_REPORT_PRODUCTS,
    organizationId,
    filters,
    {
      page: table.page,
      per_page: table.perPage,
      sort_by: table.sortBy,
      sort_direction: table.sortDirection
    },
    signal
  );
}

export function getOrganizationSalesPeriods(organizationId, filters, signal) {
  return organizationSalesReportRequest(
    ENDPOINT_ORGANIZATION_SALES_REPORT_PERIODS,
    organizationId,
    filters,
    {},
    signal
  );
}

export function getOrganizationSalesActivity(organizationId, filters, signal) {
  const headersAndParams = getTokenHeaders();
  return getAxios(false).get(
    addParam(ENDPOINT_ORGANIZATION_SALES_REPORT_ACTIVITY, '{organization}', organizationId),
    {
      headers: headersAndParams.headers,
      params: buildActivityReportParams(filters),
      paramsSerializer: params => qs.stringify(params, { arrayFormat: 'brackets' }),
      signal
    }
  ).then(response => response && response.data ? response.data : null).catch(e => {
    if (e && (e.code === 'ERR_CANCELED' || e.name === 'CanceledError')) throw e;
    errorCatch(e, {
      401: Messages.UNAUTHENTICATED_ERROR,
      403: Messages.UNAUTHORIZED_ERROR,
      404: Messages.NOT_FOUND_ERROR,
    });
    throw e;
  });
}

/**
 * Fetch the global sales summary. Monetary values are returned untouched as
 * exact minor-unit objects; presentation code is responsible only for display.
 *
 * @param {object} filters
 * @param {AbortSignal} [signal]
 * @returns {Promise<object|null>}
 */
export function getSalesSummary(filters, signal) {
  const headersAndParams = getTokenHeaders();

  return getAxios(false).get(ENDPOINT_SALES_REPORT_SUMMARY, {
    headers: headersAndParams.headers,
    params: buildSalesReportParams(filters),
    paramsSerializer: params => qs.stringify(params, { arrayFormat: 'brackets' }),
    signal
  })
    .then(response => response && response.data ? response.data : null)
    .catch(e => {
      if (e && (e.code === 'ERR_CANCELED' || e.name === 'CanceledError')) {
        throw e;
      }

      errorCatch(e, {
        401: Messages.UNAUTHENTICATED_ERROR,
        403: Messages.UNAUTHORIZED_ERROR,
        404: Messages.NOT_FOUND_ERROR,
      });

      throw e;
    });
}

/**
 * Fetch the compact administrator dashboard. It deliberately uses the same
 * stable 30-day sales-filter contract as Reports, while the endpoint returns
 * the dashboard-specific current and previous-period aggregates in one
 * request. Exact money and integer strings are passed through untouched.
 *
 * @param {object} filters
 * @param {AbortSignal} [signal]
 * @returns {Promise<object|null>}
 */
export function getDashboardReport(filters, signal) {
  const headersAndParams = getTokenHeaders();

  return getAxios(false).get(ENDPOINT_DASHBOARD_REPORT, {
    headers: headersAndParams.headers,
    params: buildSalesReportParams(filters),
    paramsSerializer: params => qs.stringify(params, { arrayFormat: 'brackets' }),
    signal
  })
    .then(response => response && response.data ? response.data : null)
    .catch(e => {
      if (e && (e.code === 'ERR_CANCELED' || e.name === 'CanceledError')) {
        throw e;
      }

      errorCatch(e, {
        401: Messages.UNAUTHENTICATED_ERROR,
        403: Messages.UNAUTHORIZED_ERROR,
        404: Messages.NOT_FOUND_ERROR,
      });

      throw e;
    });
}

/**
 * Fetch the product sales breakdown. Pagination and sorting are performed by
 * Admin after Core has produced the complete exact aggregation.
 *
 * @param {object} filters
 * @param {{page: number, perPage: number, sortBy: string, sortDirection: string}} table
 * @param {AbortSignal} [signal]
 * @returns {Promise<object|null>}
 */
export function getSalesProducts(filters, table, signal) {
  const headersAndParams = getTokenHeaders();

  return getAxios(false).get(ENDPOINT_SALES_REPORT_PRODUCTS, {
    headers: headersAndParams.headers,
    params: {
      ...buildSalesReportParams(filters),
      page: table.page,
      per_page: table.perPage,
      sort_by: table.sortBy,
      sort_direction: table.sortDirection
    },
    paramsSerializer: params => qs.stringify(params, { arrayFormat: 'brackets' }),
    signal
  })
    .then(response => response && response.data ? response.data : null)
    .catch(e => {
      if (e && (e.code === 'ERR_CANCELED' || e.name === 'CanceledError')) {
        throw e;
      }

      errorCatch(e, {
        401: Messages.UNAUTHENTICATED_ERROR,
        403: Messages.UNAUTHORIZED_ERROR,
        404: Messages.NOT_FOUND_ERROR,
      });

      throw e;
    });
}

/**
 * Fetch the complete time-bucket breakdown. The periods endpoint follows the
 * common sales-report filter contract and deliberately does not expose table
 * pagination or sorting parameters.
 *
 * @param {object} filters
 * @param {AbortSignal} [signal]
 * @returns {Promise<object|null>}
 */
export function getSalesPeriods(filters, signal) {
  const headersAndParams = getTokenHeaders();

  return getAxios(false).get(ENDPOINT_SALES_REPORT_PERIODS, {
    headers: headersAndParams.headers,
    params: buildSalesReportParams(filters),
    paramsSerializer: params => qs.stringify(params, { arrayFormat: 'brackets' }),
    signal
  })
    .then(response => response && response.data ? response.data : null)
    .catch(e => {
      if (e && (e.code === 'ERR_CANCELED' || e.name === 'CanceledError')) {
        throw e;
      }

      errorCatch(e, {
        401: Messages.UNAUTHENTICATED_ERROR,
        403: Messages.UNAUTHORIZED_ERROR,
        404: Messages.NOT_FOUND_ERROR,
      });

      throw e;
    });
}

/**
 * Fetch the organization sales breakdown. The endpoint supports the same
 * stable server-side paging and sorting contract as the product breakdown.
 * Exact minor-unit decimal strings are returned without numeric coercion.
 *
 * @param {object} filters
 * @param {{page: number, perPage: number, sortBy: string, sortDirection: string}} table
 * @param {AbortSignal} [signal]
 * @returns {Promise<object|null>}
 */
export function getSalesOrganizations(filters, table, signal) {
  const headersAndParams = getTokenHeaders();

  return getAxios(false).get(ENDPOINT_SALES_REPORT_ORGANIZATIONS, {
    headers: headersAndParams.headers,
    params: {
      ...buildSalesReportParams(filters),
      page: table.page,
      per_page: table.perPage,
      sort_by: table.sortBy,
      sort_direction: table.sortDirection
    },
    paramsSerializer: params => qs.stringify(params, { arrayFormat: 'brackets' }),
    signal
  })
    .then(response => response && response.data ? response.data : null)
    .catch(e => {
      if (e && (e.code === 'ERR_CANCELED' || e.name === 'CanceledError')) {
        throw e;
      }

      errorCatch(e, {
        401: Messages.UNAUTHENTICATED_ERROR,
        403: Messages.UNAUTHORIZED_ERROR,
        404: Messages.NOT_FOUND_ERROR,
      });

      throw e;
    });
}

export function getInfoCardsData(payload) {
  if (payload && payload.disablePagination === true) {
    if (! payload.pagination) {
      payload.pagination = {};
    }

    payload.pagination.per_page = 100000;
  }

  return getAxios(false).get(
    addParam(
      ENDPOINT_INFO_CARDS,
      {
        '{infoCards}': '',
      }
    ),
    getTokenHeaders(payload?.pagination || null)
  )
    .then(response => {
      return response && response.data ? response.data : null
    })
    .catch(
      e => {
        errorCatch(e, {
          401: Messages.UNAUTHENTICATED_ERROR,
          403: Messages.UNAUTHORIZED_ERROR,
          404: Messages.NOT_FOUND_ERROR,
        });

        return e.response;
      }
    );
}
